import styled, { css, DefaultTheme } from 'styled-components';
import { getHorizontalInputPadding } from '@mews-ui/theme';
import { TokenName } from '@mewssystems/design-tokens';
import { Disableable, getTextTokenPreset, getToken, Size } from '@optimus-web/common';
import theme from '@optimus-web/theme';
import { Affix } from '../common/Affix';
import { Icon } from '../Icon';
import { Stack } from '../Stack';
import {
    getContainerPadding,
    getInputColor,
    getInputContainerHeight,
    getInputPadding,
    getPlaceholderPadding,
} from './getInputStyles';

export interface InputContainerProps extends Disableable {
    inputSize?: Size;
    readOnly?: boolean;
}

export const InputContainer = styled.div<InputContainerProps>`
    height: calc(${getInputContainerHeight}px - ${getToken(TokenName.BORDER_WIDTH_150)}px * 2);
    display: flex;
    flex-wrap: nowrap;
    width: 100%;
    box-sizing: border-box;
`;

export const LeftSide = styled(Stack)<{ inputSize?: Size }>`
    padding-left: ${getContainerPadding}px;
`;

export const RightSide = styled(Stack)<{ inputSize?: Size }>`
    padding-right: ${getContainerPadding}px;
`;

interface InputElementProps {
    alignRight?: boolean;
    disabled?: boolean;
    inputSize?: Size;
    size?: Size;
    readOnly?: boolean;
    hasLeftSide?: boolean;
    hasRightSide?: boolean;
}

export const InputElement = styled.input<InputElementProps>`
    ${getTextTokenPreset('BODY_M')};
    color: ${getInputColor};
    background-color: transparent;
    width: 100%;
    box-sizing: border-box;
    padding-top: ${getInputPadding}px;
    padding-bottom: ${getInputPadding}px;
    padding-left: ${(props) => (props.hasLeftSide ? getToken(TokenName.SPACING_50) : getContainerPadding(props))}px;
    padding-right: ${(props) => (props.hasRightSide ? getToken(TokenName.SPACING_50) : getContainerPadding(props))}px;
    border: 0;
    border-radius: calc(${getToken(TokenName.SPACING_25)}px + ${getToken(TokenName.SPACING_50)}px);
    outline: none;
    display: block;
    margin: 0;
    appearance: none;
    ${({ readOnly }) =>
        readOnly &&
        css`
            height: 100%;
        `}
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
    -webkit-text-fill-color: ${getInputColor};

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        appearance: none;
        margin: 0;
    }

    &[type='number'] {
        appearance: textfield;
    }

    &:invalid {
        box-shadow: none;
    }

    &:-moz-submit-invalid {
        box-shadow: none;
    }

    &:-moz-ui-invalid {
        box-shadow: none;
    }

    &::-ms-clear {
        display: none;
        width: 0;
        height: 0;
    }
    text-align: ${(props) => (props.alignRight ? 'right' : 'left')};
`;

export const InputWrapper = styled.div`
    width: 100%;
    position: relative;
    justify-content: center;
    align-items: center;
    display: flex;
`;

export const Prefix = styled(Affix)``;

export const Suffix = styled(Affix)`
    margin-left: ${getToken(TokenName.SPACING_50)}px;
`;

export interface PlaceholderElementProps extends Disableable {
    children?: React.ReactNode;
    inputSize?: Size;
    disabled?: boolean;
    $readOnly?: boolean;
    hasLeftSide?: boolean;
    hasRightSide?: boolean;
}

export const PlaceholderElement = styled.div<PlaceholderElementProps>`
    ${getTextTokenPreset('BODY_M')};
    color: ${({ disabled }) =>
        disabled ? getToken(TokenName.TEXT_DISABLED) : getToken(TokenName.TEXT_STATIC_TERTIARY)};
    position: absolute;
    pointer-events: none;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    box-sizing: border-box;
    padding-left: ${(props) => (props.hasLeftSide ? getToken(TokenName.SPACING_50) : getContainerPadding(props))}px;
    padding-right: ${(props) => (props.hasRightSide ? getToken(TokenName.SPACING_50) : getContainerPadding(props))}px;

    ${({ $readOnly }) =>
        $readOnly &&
        css`
            padding-top: ${getPlaceholderPadding}px;
            padding-bottom: ${getPlaceholderPadding}px;
            position: relative;
        `}
`;

export const getPaymentInputStyles = (themeProvided: DefaultTheme = theme) => `
    ${getTextTokenPreset('BODY_M')({ theme: themeProvided })};
    color: ${getInputColor({ theme: themeProvided })};
    border: 0;
    display: block;
    outline: none;
    margin: 0;
    padding: 0 ${getHorizontalInputPadding({ theme: themeProvided })}px;
    font-feature-settings: 'calt' on, 'tnum' on, 'ss01' on, 'ss03' on, 'cv01' on, 'cv02' on, 'cv03' on, 'cv04' on, 'cv05' on, 'cv06' on, 'cv07' on, 'cv08' on, 'cv09' on, 'cv10' on, 'zero' on;
`;

export const IndicativeIconWrapper = styled(Icon)<{ $disabled?: boolean }>`
    color: ${({ $disabled }) =>
        $disabled ? getToken(TokenName.TEXT_DISABLED) : getToken(TokenName.TEXT_STATIC_PRIMARY)};
    padding-right: ${getToken(TokenName.SPACING_50)}px;
`;
